<template>
  <div class="login-page">
    <!-- Painel esquerdo — branding -->
    <div class="login-left">
      <div class="login-left__inner">
        <div class="login-mark" aria-hidden="true">
          <svg
            viewBox="0 0 48 48"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="3"
              y="3"
              width="19"
              height="19"
              rx="4"
              fill="white"
              opacity="0.9"
            />
            <rect
              x="26"
              y="3"
              width="19"
              height="19"
              rx="4"
              fill="white"
              opacity="0.4"
            />
            <rect
              x="3"
              y="26"
              width="19"
              height="19"
              rx="4"
              fill="white"
              opacity="0.4"
            />
            <rect x="26" y="26" width="19" height="19" rx="4" fill="#c9956c" />
          </svg>
        </div>

        <p class="login-system-label">Sistema de Gestão</p>
        <h1>Bem-vindo</h1>
        <p class="login-subtitle">
          Gerencie pedidos, clientes e produção em um só lugar.
        </p>

        <div class="login-features">
          <div class="feature-item">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path d="M2 11l3-3 2 2 4-5 3 3" />
            </svg>
            <span>Controle de pedidos e clientes</span>
          </div>
          <div class="feature-item">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <rect x="2" y="2" width="5" height="5" rx="1" />
              <rect x="9" y="2" width="5" height="5" rx="1" />
              <rect x="2" y="9" width="5" height="5" rx="1" />
              <rect x="9" y="9" width="5" height="5" rx="1" />
            </svg>
            <span>Gestão de estoque e materiais</span>
          </div>
          <div class="feature-item">
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
              width="14"
              height="14"
              aria-hidden="true"
            >
              <path d="M8 2v4l2.5 1.5M14 8A6 6 0 112 8a6 6 0 0112 0z" />
            </svg>
            <span>Relatórios e acompanhamento</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Painel direito — formulário -->
    <div class="login-right">
      <div class="login-card">
        <div class="login-card__header">
          <h2>Acesse sua conta</h2>
          <p>Informe suas credenciais para continuar</p>
        </div>

        <form
          class="login-form"
          novalidate
          @submit.prevent="handleLogin(false)"
        >
          <div class="form-group">
            <label for="email">E-mail</label>
            <div class="input-wrapper">
              <svg
                class="input-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path
                  d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                />
              </svg>
              <input
                id="email"
                v-model="email"
                type="email"
                name="email"
                required
                placeholder="seu@email.com"
                autocomplete="email"
                inputmode="email"
                :class="{ 'input--error': error }"
                @input="clearError"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="password">Senha</label>
            <div class="input-wrapper">
              <svg
                class="input-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path
                  d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z"
                />
              </svg>
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                required
                placeholder="••••••••"
                autocomplete="current-password"
                :class="{ 'input--error': error }"
                @input="clearError"
              />
              <button
                type="button"
                class="input-toggle-password"
                :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
                @click="showPassword = !showPassword"
              >
                <svg
                  v-if="!showPassword"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                <svg
                  v-else
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.75"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path
                    d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"
                  />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              </button>
            </div>
          </div>

          <div class="login-options">
            <label class="checkbox-remember">
              <input v-model="lembrarSenha" type="checkbox" />
              <span>{{
                biometricoDisponivel
                  ? "Lembrar acesso e entrar com digital"
                  : "Lembrar e-mail"
              }}</span>
            </label>
            <a
              href="https://wa.me/5543999343201"
              target="_blank"
              rel="noopener noreferrer"
              class="login-forgot"
            >
              Esqueceu a senha?
            </a>
          </div>

          <div v-if="error" class="login-error" role="alert" aria-live="polite">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
            >
              <path
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
            {{ error }}
          </div>

          <button type="submit" class="login-submit" :disabled="loading">
            <svg
              v-if="loading"
              class="spin"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              aria-hidden="true"
            >
              <path d="M12 3a9 9 0 100 18A9 9 0 0012 3z" opacity=".25" />
              <path d="M12 3a9 9 0 019 9" stroke-linecap="round" />
            </svg>
            <span>{{ loading ? "Entrando..." : "Entrar" }}</span>
          </button>
        </form>

        <!-- Botão biométrico -->
        <button
          v-if="biometricoDisponivel && credenciaisSalvas"
          class="login-biometric"
          type="button"
          :disabled="loading"
          @click="loginComBiometrico"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path
              d="M12 11c0-1.1.9-2 2-2s2 .9 2 2v1M8 11V9a4 4 0 018 0v2M5 12a7 7 0 0014 0"
            />
            <path d="M12 15v2M9 18a3 3 0 006 0" />
          </svg>
          <span>Entrar com digital</span>
        </button>

        <div class="login-footer">
          <a
            href="https://wa.me/5543999343201"
            target="_blank"
            rel="noopener noreferrer"
          >
            Precisa de ajuda?
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/services/api";
import { isNativeApp } from "@/utils/platform";

const PREF_KEY = "login_saved";
const BIO_CRED_KEY = "bio_credentials";

export default {
  name: "LoginPage",
  data() {
    return {
      email: "",
      password: "",
      showPassword: false,
      loading: false,
      error: null,
      lembrarSenha: false,
      biometricoDisponivel: false,
      credenciaisSalvas: false,
    };
  },
  async mounted() {
    if (isNativeApp()) {
      await this._carregarPreferencias();
      await this._verificarBiometrico();
    } else {
      const salvo = localStorage.getItem(PREF_KEY);
      if (salvo) {
        try {
          const { email } = JSON.parse(salvo);
          this.email = email || "";
          this.lembrarSenha = true;
        } catch {
          // ignore parse error
        }
      }
    }
  },
  methods: {
    clearError() {
      if (this.error) this.error = null;
    },
    async _carregarPreferencias() {
      try {
        const { Preferences } = await import("@capacitor/preferences");
        const { value } = await Preferences.get({ key: PREF_KEY });
        if (value) {
          const { email } = JSON.parse(value);
          this.email = email || "";
          this.lembrarSenha = true;
        }
      } catch {
        // preferences não disponível
      }
    },
    async _verificarBiometrico() {
      try {
        const { BiometricAuth } = await import(
          "@aparajita/capacitor-biometric-auth"
        );
        const info = await BiometricAuth.checkBiometry();
        this.biometricoDisponivel = info.isAvailable;

        if (info.isAvailable) {
          const { SecureStoragePlugin } = await import(
            "capacitor-secure-storage-plugin"
          );
          try {
            const { value } = await SecureStoragePlugin.get({
              key: BIO_CRED_KEY,
            });
            this.credenciaisSalvas = !!value;
          } catch {
            this.credenciaisSalvas = false;
          }
        }
      } catch {
        this.biometricoDisponivel = false;
      }
    },
    async loginComBiometrico() {
      this.error = null;
      this.loading = true;
      try {
        const { BiometricAuth } = await import(
          "@aparajita/capacitor-biometric-auth"
        );
        await BiometricAuth.authenticate({
          reason: "Acesse sua conta",
          cancelTitle: "Cancelar",
        });

        const { SecureStoragePlugin } = await import(
          "capacitor-secure-storage-plugin"
        );
        let value;
        try {
          ({ value } = await SecureStoragePlugin.get({ key: BIO_CRED_KEY }));
        } catch {
          throw new Error("no_credentials");
        }
        if (!value) throw new Error("no_credentials");
        const { email, password } = JSON.parse(value);
        this.email = email;
        this.password = password;
        await this.handleLogin(true);
      } catch (e) {
        // cancelar a digital não é erro (o plugin manda o motivo em .code)
        const cancelou = ["userCancel", "appCancel", "systemCancel"].includes(
          e?.code
        );
        if (!cancelou && e?.message !== "no_credentials") {
          this.error = "Autenticação biométrica falhou.";
        }
        this.loading = false;
      }
    },
    // skipCredentialSave: true só no login pela digital. O submit do form
    // passa false explícito — sem isso o Vue mandava o evento como argumento,
    // que é "verdadeiro", e a escolha de lembrar nunca era salva.
    async handleLogin(skipCredentialSave = false) {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.post("/login", {
          user: { email: this.email, password: this.password },
        });

        const { user, auth_token, refresh_token, companies } = response.data;

        if (!skipCredentialSave && this.lembrarSenha) {
          const payload = JSON.stringify({ email: this.email });
          if (isNativeApp()) {
            try {
              const { Preferences } = await import("@capacitor/preferences");
              await Preferences.set({ key: PREF_KEY, value: payload });
              const { SecureStoragePlugin } = await import(
                "capacitor-secure-storage-plugin"
              );
              await SecureStoragePlugin.set({
                key: BIO_CRED_KEY,
                value: JSON.stringify({
                  email: this.email,
                  password: this.password,
                }),
              });
              this.credenciaisSalvas = true;
            } catch {
              // ignore
            }
          } else {
            localStorage.setItem(PREF_KEY, payload);
          }
        } else if (!skipCredentialSave && !this.lembrarSenha) {
          if (isNativeApp()) {
            try {
              const { Preferences } = await import("@capacitor/preferences");
              await Preferences.remove({ key: PREF_KEY });
              const { SecureStoragePlugin } = await import(
                "capacitor-secure-storage-plugin"
              );
              await SecureStoragePlugin.remove({ key: BIO_CRED_KEY });
              this.credenciaisSalvas = false;
            } catch {
              // ignore
            }
          } else {
            localStorage.removeItem(PREF_KEY);
          }
        }

        this.$store.commit("SET_AUTH", {
          token: auth_token,
          refreshToken: refresh_token,
          user,
          companies,
        });
        this.$router.push("/selecionar-empresa");
      } catch (err) {
        if (!err.response) {
          this.error =
            "Erro de conexão. Verifique sua internet e tente novamente.";
        } else {
          this.error = err.response.data?.error || "E-mail ou senha inválidos.";
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.login-page {
  display: flex;
  min-height: 100vh;
}

/* ── Painel esquerdo ─────────────────────────────────────────────────────── */
.login-left {
  flex: 1;
  background: linear-gradient(
    145deg,
    var(--c-brand-panel-1) 0%,
    var(--c-brand-panel-2) 45%,
    var(--c-brand-panel-3) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  position: relative;
  overflow: hidden;
}

.login-left::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.07) 1px,
    transparent 1px
  );
  background-size: 28px 28px;
  pointer-events: none;
}

.login-left::after {
  content: "";
  position: absolute;
  width: 340px;
  height: 340px;
  background: radial-gradient(circle, var(--c-brand-glow) 0%, transparent 70%);
  border-radius: 50%;
  bottom: -80px;
  right: -80px;
  pointer-events: none;
}

.login-left__inner {
  position: relative;
  z-index: 1;
  color: white;
  max-width: 380px;
}

.login-mark {
  margin-bottom: 2rem;
}

.login-mark svg {
  width: 56px;
  height: 56px;
  filter: drop-shadow(0 4px 16px var(--c-brand-icon));
}

.login-system-label {
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(201, 149, 108, 0.8);
  margin-bottom: 0.5rem;
}

.login-left__inner h1 {
  font-family: var(--font-brand);
  font-size: 2.75rem;
  font-weight: 700;
  color: var(--c-brand-text);
  letter-spacing: 0.01em;
  margin-bottom: 0.625rem;
  text-shadow: 0 2px 12px var(--c-brand-icon);
}

.login-subtitle {
  font-size: var(--text-base);
  color: var(--c-brand-text-sub);
  margin-bottom: 2.5rem;
  line-height: 1.6;
}

.login-features {
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9375rem;
  color: var(--c-brand-text-sub);
  font-weight: 500;
}

.feature-item svg {
  flex-shrink: 0;
  color: var(--c-accent);
  opacity: 0.9;
}

/* ── Painel direito ──────────────────────────────────────────────────────── */
.login-right {
  width: 480px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--c-bg);
}

.login-card {
  width: 100%;
  max-width: 400px;
  background: var(--c-surface);
  border-radius: var(--r-xl);
  padding: 2.5rem;
  box-shadow: var(--sh-md);
  border: 1px solid var(--c-border-light);
}

.login-card__header {
  margin-bottom: 2rem;
}

.login-card__header h2 {
  font-size: var(--text-2xl);
  font-weight: 700;
  color: var(--c-text);
  letter-spacing: -0.02em;
  margin-bottom: 0.375rem;
}

.login-card__header p {
  font-size: var(--text-sm);
  color: var(--c-text-muted);
}

/* ── Formulário ──────────────────────────────────────────────────────────── */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.login-form .form-group {
  margin-bottom: 1.125rem;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--c-text-muted);
  pointer-events: none;
}

.input-wrapper input {
  padding-left: 2.625rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.input-wrapper input.input--error {
  border-color: var(--c-danger-border);
  box-shadow: 0 0 0 3px rgba(146, 40, 40, 0.08);
}

.input-toggle-password {
  position: absolute;
  right: 0.875rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--c-text-muted);
  display: flex;
  align-items: center;
  transition: color 0.2s;
}

.input-toggle-password:hover {
  color: var(--c-text);
}

.input-toggle-password svg {
  width: 17px;
  height: 17px;
}

/* ── Opções (lembrar + esqueci) ──────────────────────────────────────────── */
.login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.checkbox-remember {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--text-sm);
  color: var(--c-text-muted);
  cursor: pointer;
  user-select: none;
}

.checkbox-remember input[type="checkbox"] {
  appearance: checkbox;
  -webkit-appearance: checkbox;
  width: 17px;
  height: 17px;
  min-width: 17px;
  accent-color: var(--c-accent);
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: none;
  box-shadow: none;
}

.login-forgot {
  font-size: var(--text-sm);
  color: var(--c-accent);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.login-forgot:hover {
  color: var(--c-accent-dark);
  text-decoration: underline;
}

/* ── Erro ────────────────────────────────────────────────────────────────── */
.login-error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--c-danger-bg);
  color: var(--c-danger);
  border: 1px solid var(--c-danger-border);
  border-radius: var(--r-sm);
  font-size: var(--text-sm);
  font-weight: 500;
  margin-bottom: 1rem;
  animation: shake 0.3s ease;
}

.login-error svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-4px);
  }
  40% {
    transform: translateX(4px);
  }
  60% {
    transform: translateX(-3px);
  }
  80% {
    transform: translateX(3px);
  }
}

/* ── Botão submit ────────────────────────────────────────────────────────── */
.login-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.8125rem 1.25rem;
  background: var(--c-accent);
  color: white;
  border: none;
  border-radius: var(--r-md);
  font-family: var(--font);
  font-size: var(--text-base);
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 0.375rem;
  letter-spacing: 0.01em;
}

.login-submit:hover:not(:disabled) {
  background: var(--c-accent-dark);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px var(--c-accent-glow);
}

.login-submit:active:not(:disabled) {
  transform: translateY(0);
}

.login-submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.login-submit svg {
  width: 18px;
  height: 18px;
}

.spin {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ── Biométrico ──────────────────────────────────────────────────────────── */
.login-biometric {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  width: 100%;
  margin-top: 0.75rem;
  padding: 0.75rem 1.25rem;
  background: transparent;
  color: var(--c-accent);
  border: 1px solid var(--c-accent-light);
  border-radius: var(--r-md);
  font-family: var(--font);
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.login-biometric:hover:not(:disabled) {
  background: var(--c-accent-xlt);
  border-color: var(--c-accent);
}

.login-biometric svg {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

/* ── Footer ──────────────────────────────────────────────────────────────── */
.login-footer {
  margin-top: 1.5rem;
  text-align: center;
}

.login-footer a {
  font-size: var(--text-sm);
  color: var(--c-text-muted);
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;
}

.login-footer a:hover {
  color: var(--c-accent);
}

/* ── Mobile ──────────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .login-page {
    flex-direction: column;
    height: 100vh;
    height: 100dvh;
    overflow: hidden;
  }

  .login-left {
    flex: 0 0 auto;
    padding: 1rem 1.25rem;
    background: linear-gradient(
      90deg,
      var(--c-brand-panel-1) 0%,
      var(--c-brand-panel-2) 100%
    );
  }

  .login-left__inner {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 0.875rem;
    max-width: none;
  }

  .login-mark {
    margin-bottom: 0;
    flex-shrink: 0;
  }

  .login-mark svg {
    width: 36px;
    height: 36px;
  }

  .login-system-label,
  .login-subtitle,
  .login-features {
    display: none;
  }

  .login-left__inner h1 {
    font-size: 1.375rem;
    margin-bottom: 0;
  }

  .login-right {
    width: 100%;
    flex: 1;
    padding: 1.25rem 1rem;
    overflow-y: auto;
    align-items: flex-start;
  }

  .login-card {
    padding: 1.5rem 1.25rem;
    box-shadow: none;
    border: 1px solid var(--c-border-light);
  }

  .login-card__header {
    margin-bottom: 1.25rem;
  }

  .login-card__header h2 {
    font-size: var(--text-xl);
  }

  .login-card__header p {
    font-size: var(--text-sm);
  }

  .login-form .form-group {
    margin-bottom: 0.875rem;
  }

  .login-footer {
    margin-top: 1rem;
  }
}
</style>
